import { ColDef, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";

import { AddCustomModelPriceAction } from "./AddCustomModelPriceAction";
import { SuggestedModelPrices } from "./SuggestedModelPrices";

import { formatPrice } from "@/components/ModelDropdown/utils";
import { DataTable } from "@/components/ui/ag-grid-table";
import { TableColumn } from "@/components/ui/TableV2";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import {
  CustomModelPrice,
  useCustomModelPrices,
  useDeleteCustomModelPrice,
} from "@/queries/customModelPrices";

const formatRate = (value: number | null | undefined): string =>
  value === undefined || value === null ? "–" : (formatPrice(value) ?? "–");

// A modality sub-column (Text / Audio / Image) under an Input or Output group.
// Pass columnGroupShow="open" to hide the column when the group is collapsed.
const modalityChild = (
  headerName: string,
  pick: (row: CustomModelPrice) => number | undefined,
  columnGroupShow?: "open",
): ColDef<CustomModelPrice> => ({
  headerName,
  cellClass: "tabular-nums",
  cellStyle: { paddingLeft: "var(--ag-cell-horizontal-padding)" },
  flex: 1,
  minWidth: 72,
  sortable: false,
  resizable: false,
  suppressHeaderMenuButton: true,
  columnGroupShow,
  valueGetter: ({ data }: ValueGetterParams<CustomModelPrice>) => (data ? pick(data) : undefined),
  valueFormatter: ({ value }: ValueFormatterParams<CustomModelPrice, number | undefined>) =>
    formatRate(value),
});

export const CustomModelPrices = () => {
  const auth = useAuth();
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId
    ? String(userContext.activeWorkspaceId)
    : undefined;

  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const { data } = useCustomModelPrices(auth?.userToken ?? "", workspaceId);
  const { mutate: deleteCustomPrice } = useDeleteCustomModelPrice(
    auth?.userToken ?? "",
    workspaceId,
  );

  const prices = data?.custom_model_prices ?? [];

  const columns = [
    {
      id: 0,
      header: "Model",
      field: "model_name",
      flex: 2,
      minWidth: 220,
    },
    {
      id: 1,
      header: "Input / 1M",
      groupId: "input",
      openByDefault: true,
      children: [
        modalityChild("Text", row => row.prices.input.text_tokens),
        modalityChild("Audio", row => row.prices.input.audio_tokens, "open"),
        modalityChild("Image", row => row.prices.input.image_tokens, "open"),
        modalityChild("Cache read", row => row.prices.input.cache_read_tokens, "open"),
      ],
    },
    {
      id: 2,
      header: "Output / 1M",
      groupId: "output",
      openByDefault: true,
      children: [
        modalityChild("Text", row => row.prices.output.text_tokens),
        modalityChild("Audio", row => row.prices.output.audio_tokens, "open"),
        modalityChild("Image", row => row.prices.output.image_tokens, "open"),
      ],
    },
  ] as unknown as TableColumn<CustomModelPrice>[];

  return (
    <div className="flex h-full flex-col">
      <SuggestedModelPrices />
      <DataTable<CustomModelPrice>
        data={prices}
        columns={columns}
        // Dividers via CSS vars, not themeQuartz.withParams; theme objects break across ag-grid copies in prod builds
        className="[--ag-column-border:solid_1px_var(--ag-border-color)] [--ag-header-column-border:solid_1px_var(--ag-border-color)]"
        action={<AddCustomModelPriceAction />}
        emptyStateMessage="No custom model prices yet"
        fullHeight
        suppressHeaderMenuButton
        rowsDeletable
        onRequestDeleteConfirm={onRequestDeleteConfirm}
        onDelete={price => {
          if (price) {
            deleteCustomPrice(price.id);
          }
        }}
        objectName="custom price"
      />
    </div>
  );
};
